<template>
  <div v-if="error" class="flex grow items-center justify-center">
    <div class="text-red-500">{{ error }}</div>
  </div>
  <div v-else id="PublicGuide" class="text-xs sm:text-base">
    <header class="my-4 flex flex-col items-center gap-2 p-6 print:my-0">
      <h1
        class="flex gap-2 bg-gradient-to-b from-black/90 to-black/50 bg-cover bg-clip-text text-center text-3xl font-extrabold tracking-tight text-transparent sm:text-4xl lg:py-1 lg:text-5xl"
      >
        <Icon v-if="isLoading" icon="spinner" class="w-6" />
        {{ video?.name || "Loading Guide…" }}
      </h1>
      <div class="flex items-center text-black/40 max-sm:flex-col">
        <div class="mr-3" :class="[video?.creatorName ? '' : isLoading ? 'opacity-0' : '']">
          Recorded by {{ video?.creatorName }}
          <template v-if="humanRecordingTime">in {{ humanRecordingTime }}</template>
        </div>
        <div v-if="video" class="border-black/10 pl-3 sm:border-l">
          {{ pluralize(steps.length, "step") }}
        </div>
      </div>
    </header>

    <div class="flex justify-center print:hidden">
      <iframe
        v-if="video"
        :src="video.embedUrl"
        frameborder="0"
        allowfullscreen
        class="aspect-video w-full shadow-2xl lg:min-h-[300px] lg:max-w-4xl lg:overflow-hidden lg:rounded-xl"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
      ></iframe>
      <div
        v-else
        class="flex aspect-video w-full items-center justify-center bg-black/70 text-white shadow-2xl lg:min-h-[300px] lg:max-w-4xl lg:overflow-hidden lg:rounded-xl"
      >
        <Icon v-if="isLoading" icon="spinner" class="w-6" />
      </div>
    </div>

    <h2
      class="mt-16 bg-gradient-to-b from-black/90 to-black/50 bg-cover bg-clip-text text-center text-2xl font-extrabold tracking-tight text-transparent print:mt-0 print:hidden sm:text-3xl lg:py-1 lg:text-4xl"
    >
      Step-by-Step Guide
    </h2>

    <Guide
      v-if="video"
      id="Guide"
      :video="video"
      :video-dimensions="videoDimensions"
      :base-file-url="video.baseFileUrl"
      :actions="actionsForGuide"
      :voice-overs="voiceOvers"
      :selected-action-ids="[]"
      :show-chapter-navigation="false"
      class="w-full"
    />

    <GuideCreationBanner
      v-if="video"
      class=""
      :creator-name="video.creatorName"
      :recording-time="video.totalRecordingTime || 0"
      :href="`https://www.recordonce.com/?ref=guide/${video?.token}`"
    />

    <footer v-if="false" class="m-2 flex items-center justify-center gap-2.5 p-8">
      <div class="text-black/40">created with</div>
      <a
        :href="`https://recordonce.com/?ref=guide/${video?.token}`"
        target="_blank"
        class="-m-1.5 flex items-center justify-center gap-1.5 rounded-lg p-1.5 pr-2.5 transition-all hover:bg-black/10"
      >
        <img src="@assets/images/Record-Once-icon-64x64px.svg" class="block h-6 w-6" />
        <div class="font-extrabold tracking-tight">Record Once</div>
      </a>
    </footer>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onMounted,
  onBeforeMount,
  onBeforeUnmount,
  watch,
  provide,
  ref,
  type Ref,
  reactive,
  defineProps,
} from "vue";

import Guide from "@guide/Guide.vue";
import Icon from "@components/Icon.vue";
import GuideCreationBanner from "@components/GuideCreationBanner.vue";
import Video from "@models/Video";
import { Action, BrowserAction } from "@models/ActionHierarchy";
import VoiceOver from "@models/VoiceOver";
import { getErrorMessage } from "@modules/errors";
import { numberToHumanDuration, pluralize } from "@modules/helpers";

const props = defineProps<{
  videoToken: string;
}>();

const isLoading = ref(true);
const error: Ref<null | string> = ref(null);

const video = ref<Video | undefined>();
// const actions = ref<Action[] | undefined>([]);
// const voiceOvers = ref<VoiceOver[] | undefined>([]);
const actions = computed<Action[]>(() => {
  if (!video.value) return [];
  return Action.query().where("videoId", video.value.id).orderBy("index").all();
});
const voiceOvers = computed<VoiceOver[]>(() => {
  if (!video.value) return [];
  return VoiceOver.queryForVideo(video.value.id).all();
});

onBeforeMount(async () => {
  try {
    video.value = await Video.fetch(props.videoToken);
    if (!video.value) {
      error.value = `404 - Video not found ${props.videoToken}`;
      throw new Error(`404 - Video not found ${props.videoToken}`);
    }
    // actions.value = Action.query().where("videoId", video.value.id).orderBy("index").all();
    // voiceOvers.value = VoiceOver.queryForVideo(video.value.id).all() as VoiceOver[];
  } catch (err) {
    error.value = getErrorMessage(err);
    console.error(err);
  }
  isLoading.value = false;
});

const visibleActions = computed(() => Action.getVisibleActions(actions.value));
const actionsForGuide = computed(() =>
  visibleActions.value.filter((action) => action.type !== "Pause"),
);
const steps = computed(() =>
  actionsForGuide.value.filter((action) => action instanceof BrowserAction),
);

const videoDimensions = computed(() => ({
  width: video.value?.width || 1280,
  height: video.value?.height || 720,
}));

const humanRecordingTime = computed(() => {
  if (!video.value) return null;

  const duration = video.value.totalRecordingTime;
  if (!duration) return null;
  if (duration > 15 * 60) return null;

  return numberToHumanDuration(duration).toLowerCase();
});
</script>
